import React, { useEffect, useState } from "react";
import { Save, RotateCcw, FileText, ChevronRight } from "lucide-react";
import api from "../lib/api";

const SECTION_LABELS = {
  global: "Marca e Logo",
  homepage_hero: "Homepage — Hero",
  homepage_featured: "Homepage — Caso em destaque",
  homepage_upcoming: "Homepage — Próximos casos",
  homepage_how_it_works: "Homepage — Como funciona",
  homepage_testimonials: "Homepage — Testemunhos",
  homepage_cta: "Homepage — Call to Action final",
  como_funciona_page: "Página: Como Funciona",
  loja_page: "Página: Loja",
  contacto_page: "Página: Contacto",
  footer: "Rodapé",
};

// Define editor schemas. Each entry: {key, label, type: 'text'|'textarea'|'array', itemFields?}
const SCHEMAS = {
  global: [
    { k: "brand_part1", l: "Marca — 1ª parte", type: "text" },
    { k: "brand_red", l: "Marca — parte vermelha", type: "text" },
    { k: "brand_full", l: "Marca — nome completo", type: "text" },
    { k: "marquee_text", l: "Texto do ticker (rola na hero)", type: "textarea" },
    { k: "logo_url", l: "URL do logo", type: "text" },
  ],
  homepage_hero: [
    { k: "eyebrow_classification", l: "Pré-título (classificação)", type: "text" },
    { k: "eyebrow_status", l: "Pré-título (estado)", type: "text" },
    { k: "title_line1", l: "Título — linha 1", type: "text" },
    { k: "title_red", l: "Título — palavras a vermelho", type: "text" },
    { k: "title_line3", l: "Título — linha 3", type: "text" },
    { k: "paragraph", l: "Parágrafo", type: "textarea" },
    { k: "cta_primary", l: "Botão principal", type: "text" },
    { k: "cta_secondary", l: "Botão secundário", type: "text" },
    { k: "sidebar_eyebrow", l: "Pré-título do cartão lateral", type: "text" },
    { k: "stats", l: "Estatísticas (4 cartões)", type: "array", itemFields: [
      { k: "label", l: "Etiqueta" }, { k: "value", l: "Valor" },
    ] },
  ],
  homepage_featured: [
    { k: "eyebrow_suffix", l: "Sufixo do pré-título (após //)", type: "text" },
    { k: "stamp_text", l: "Texto do carimbo", type: "text" },
    { k: "suspects_eyebrow", l: "Pré-título da secção de suspeitos", type: "text" },
    { k: "price_label", l: "Etiqueta do preço", type: "text" },
    { k: "investigate_button", l: "Botão 'Investigar'", type: "text" },
  ],
  homepage_upcoming: [
    { k: "eyebrow", l: "Pré-título", type: "text" },
    { k: "title", l: "Título", type: "text" },
    { k: "subtitle", l: "Subtítulo", type: "textarea" },
    { k: "empty_text", l: "Mensagem se não houver casos", type: "text" },
  ],
  homepage_how_it_works: [
    { k: "eyebrow", l: "Pré-título", type: "text" },
    { k: "title", l: "Título", type: "text" },
    { k: "subtitle", l: "Subtítulo", type: "textarea" },
    { k: "steps", l: "Passos", type: "array", itemFields: [
      { k: "n", l: "Número (01, 02...)" }, { k: "t", l: "Título do passo" }, { k: "d", l: "Descrição" },
    ] },
  ],
  homepage_testimonials: [
    { k: "eyebrow", l: "Pré-título", type: "text" },
    { k: "title", l: "Título", type: "text" },
    { k: "rating", l: "Avaliação (ex: 4.9 / 5)", type: "text" },
    { k: "investigators_count", l: "Contador (ex: 214 investigadores)", type: "text" },
  ],
  homepage_cta: [
    { k: "eyebrow", l: "Pré-título", type: "text" },
    { k: "title_line1", l: "Título — linha 1", type: "text" },
    { k: "title_red", l: "Título — palavras vermelhas", type: "text" },
    { k: "title_line3", l: "Título — final (ex: '.')", type: "text" },
    { k: "paragraph", l: "Parágrafo", type: "textarea" },
    { k: "button", l: "Texto do botão", type: "text" },
  ],
  como_funciona_page: [
    { k: "eyebrow", l: "Pré-título", type: "text" },
    { k: "title", l: "Título", type: "text" },
    { k: "subtitle", l: "Subtítulo", type: "textarea" },
    { k: "steps", l: "Passos", type: "array", itemFields: [
      { k: "n", l: "Número" }, { k: "t", l: "Título" }, { k: "d", l: "Descrição" },
    ] },
    { k: "faq_eyebrow", l: "FAQ — Pré-título", type: "text" },
    { k: "faq_title", l: "FAQ — Título", type: "text" },
    { k: "faq_items", l: "FAQ — Perguntas", type: "array", itemFields: [
      { k: "q", l: "Pergunta" }, { k: "a", l: "Resposta", textarea: true },
    ] },
    { k: "cta_title", l: "Título do CTA final", type: "text" },
    { k: "cta_button", l: "Botão do CTA final", type: "text" },
  ],
  loja_page: [
    { k: "eyebrow", l: "Pré-título", type: "text" },
    { k: "title", l: "Título", type: "text" },
    { k: "subtitle", l: "Subtítulo", type: "textarea" },
    { k: "available_badge", l: "Etiqueta 'Disponível'", type: "text" },
    { k: "soon_badge", l: "Etiqueta 'Em breve'", type: "text" },
  ],
  contacto_page: [
    { k: "eyebrow", l: "Pré-título", type: "text" },
    { k: "title", l: "Título", type: "text" },
    { k: "subtitle", l: "Subtítulo", type: "textarea" },
    { k: "channels", l: "Canais de contacto", type: "array", itemFields: [
      { k: "label", l: "Etiqueta (ex: Email)" }, { k: "value", l: "Valor visível" }, { k: "href", l: "Link (mailto:/https://)" },
    ] },
    { k: "faq_eyebrow", l: "FAQ — Pré-título", type: "text" },
    { k: "faq_title", l: "FAQ — Título", type: "text" },
    { k: "faq_items", l: "FAQ — Perguntas", type: "array", itemFields: [
      { k: "q", l: "Pergunta" }, { k: "a", l: "Resposta", textarea: true },
    ] },
    { k: "footer_note", l: "Nota inferior", type: "textarea" },
  ],
  footer: [
    { k: "description", l: "Descrição", type: "textarea" },
    { k: "classification", l: "Classificação (mono pequeno)", type: "text" },
    { k: "contact_email", l: "Email de contacto", type: "text" },
    { k: "contact_location", l: "Localização", type: "text" },
    { k: "ref_prefix", l: "Prefixo da referência", type: "text" },
    { k: "copyright", l: "Texto de copyright", type: "text" },
    { k: "footer_tagline", l: "Tagline final do rodapé", type: "text" },
  ],
};

export default function ContentEditor() {
  const [content, setContent] = useState({});
  const [active, setActive] = useState("homepage_hero");
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/site-content").then((r) => {
      setContent(r.data || {});
    });
  }, []);

  useEffect(() => {
    setDraft(JSON.parse(JSON.stringify(content[active] || {})));
    setMsg("");
  }, [active, content]);

  const setField = (k, v) => setDraft({ ...draft, [k]: v });

  const save = async () => {
    setBusy(true);
    setMsg("");
    try {
      await api.put(`/admin/site-content/${active}`, { value: draft });
      const newContent = { ...content, [active]: draft };
      setContent(newContent);
      setMsg("✓ Guardado. As alterações já estão no site.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Erro ao guardar.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setDraft(JSON.parse(JSON.stringify(content[active] || {})));
    setMsg("");
  };

  const schema = SCHEMAS[active] || [];

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      {/* Sidebar */}
      <aside className="lg:col-span-3">
        <p className="section-tag mb-5">Secções</p>
        <div className="space-y-px bg-white/10">
          {Object.keys(SCHEMAS).map((key) => (
            <button
              key={key}
              onClick={() => setActive(key)}
              data-testid={`content-section-${key}`}
              className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                active === key ? "bg-blood text-white" : "bg-ink-100 text-bone-dim hover:bg-ink-200"
              }`}
            >
              <span className="text-xs">{SECTION_LABELS[key] || key}</span>
              <ChevronRight className="w-3.5 h-3.5 opacity-60" />
            </button>
          ))}
        </div>
      </aside>

      {/* Editor */}
      <div className="lg:col-span-9 dossier p-6 md:p-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3 pb-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blood" />
            <h2 className="headline text-2xl">{SECTION_LABELS[active]}</h2>
          </div>
          <div className="flex gap-2">
            <button onClick={reset} className="btn-ghost text-[10px] py-2.5 px-4" data-testid="content-reset">
              <RotateCcw className="w-3.5 h-3.5" /> Repor
            </button>
            <button onClick={save} disabled={busy} className="btn-primary text-[10px] py-2.5 px-4 disabled:opacity-50" data-testid="content-save">
              <Save className="w-3.5 h-3.5" /> {busy ? "A guardar..." : "Guardar"}
            </button>
          </div>
        </div>

        {msg && (
          <p className={`mb-5 mono text-[11px] tracking-[0.2em] ${msg.startsWith("✓") ? "text-blood" : "text-bone-muted"}`}>
            {msg}
          </p>
        )}

        <div className="space-y-5">
          {schema.map((f) => (
            <FieldEditor
              key={f.k}
              field={f}
              value={draft[f.k]}
              onChange={(v) => setField(f.k, v)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FieldEditor({ field, value, onChange }) {
  if (field.type === "array") {
    const items = Array.isArray(value) ? value : [];
    return (
      <div>
        <label className="mono text-[10px] tracking-[0.3em] text-bone-faded block mb-3">{field.l.toUpperCase()}</label>
        <div className="space-y-3">
          {items.map((item, idx) => (
            <div key={idx} className="border border-white/10 p-4 bg-ink-200">
              <div className="flex items-center justify-between mb-3">
                <span className="mono text-[10px] text-blood tracking-[0.2em]">ITEM {String(idx + 1).padStart(2, "0")}</span>
                <div className="flex gap-1">
                  {idx > 0 && (
                    <button
                      onClick={() => {
                        const next = [...items];
                        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                        onChange(next);
                      }}
                      className="mono text-[10px] text-bone-muted hover:text-white px-2 py-1 border border-white/10"
                    >↑</button>
                  )}
                  {idx < items.length - 1 && (
                    <button
                      onClick={() => {
                        const next = [...items];
                        [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
                        onChange(next);
                      }}
                      className="mono text-[10px] text-bone-muted hover:text-white px-2 py-1 border border-white/10"
                    >↓</button>
                  )}
                  <button
                    onClick={() => onChange(items.filter((_, i) => i !== idx))}
                    className="mono text-[10px] text-blood hover:text-white px-2 py-1 border border-blood/40"
                  >×</button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {field.itemFields.map((itemField) => (
                  <div key={itemField.k} className={itemField.textarea ? "sm:col-span-2" : ""}>
                    <label className="mono text-[10px] tracking-[0.25em] text-bone-faded block mb-1.5">{itemField.l.toUpperCase()}</label>
                    {itemField.textarea ? (
                      <textarea
                        value={item[itemField.k] || ""}
                        onChange={(e) => {
                          const next = [...items];
                          next[idx] = { ...next[idx], [itemField.k]: e.target.value };
                          onChange(next);
                        }}
                        className="cec-input min-h-[80px] text-xs"
                      />
                    ) : (
                      <input
                        value={item[itemField.k] || ""}
                        onChange={(e) => {
                          const next = [...items];
                          next[idx] = { ...next[idx], [itemField.k]: e.target.value };
                          onChange(next);
                        }}
                        className="cec-input text-xs"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button
            onClick={() => {
              const empty = {};
              field.itemFields.forEach((f) => { empty[f.k] = ""; });
              onChange([...items, empty]);
            }}
            className="btn-ghost w-full justify-center text-[10px] py-2.5"
          >+ Adicionar item</button>
        </div>
        <style>{`.cec-input { width:100%; background:#111; border:1px solid rgba(255,255,255,0.1); padding:8px 10px; font-family:'IBM Plex Sans',sans-serif; font-size:13px; color:#fff; } .cec-input:focus { outline:none; border-color:#C8102E }`}</style>
      </div>
    );
  }

  return (
    <div>
      <label className="mono text-[10px] tracking-[0.3em] text-bone-faded block mb-2">{field.l.toUpperCase()}</label>
      {field.type === "textarea" ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="cec-input min-h-[90px]"
          data-testid={`content-field-${field.k}`}
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="cec-input"
          data-testid={`content-field-${field.k}`}
        />
      )}
      <style>{`.cec-input { width:100%; background:#111; border:1px solid rgba(255,255,255,0.1); padding:10px 12px; font-family:'IBM Plex Sans',sans-serif; font-size:14px; color:#fff; } .cec-input:focus { outline:none; border-color:#C8102E }`}</style>
    </div>
  );
}
